import FetchAiCostCalls from '@Application/AiCosts/Queries/FetchAiCostCalls';
import fetchAiCostCalls from '@Application/AiCosts/fetchAiCostCalls';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import aiCostsFiltersSignal from '@UI/Pages/Administration/AiCosts/Filters/Signals/aiCostsFiltersSignal';
import type AiCostCallsListView from '@UI/Store/AiCosts/Views/AiCostCallsListView';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { useDispatch, useStoreQuery } from '@verclam/synapse';

type Result = {
	/** The pages loaded so far for the current period; `null` until the first page arrives. */
	readonly list: AiCostCallsListView | null;
	readonly error: unknown;
	readonly refetch: () => void;
	readonly showMore: () => void;
	readonly isLoadingMore: boolean;
};

/** Same contract as `useAiCostBreakdown`, for the individual calls. */
const useAiCostCalls = (accountId: number, rows: number): Result => {
	const dispatch = useDispatch();
	const filters = useSignalValue(aiCostsFiltersSignal);
	const queryKey = filters.tabQueryParams.toString();
	const [isLoadingMore, setIsLoadingMore] = React.useState(false);

	const isCurrent = React.useCallback(
		(value: AiCostCallsListView | null): value is AiCostCallsListView =>
			value !== null && value.accountId === accountId && value.queryKey === queryKey,
		[accountId, queryKey],
	);

	const { data, error, refetch } = useStoreQuery({
		selector: (state) => state.aiCostsStore.calls,
		stopOn: isCurrent,
		query: () =>
			dispatch(fetchAiCostCalls(new FetchAiCostCalls(accountId, filters.tabQueryParams, 0, rows))),
		deps: [accountId, queryKey],
	});

	const list = isCurrent(data) ? data : null;

	const showMore = React.useCallback(() => {
		if (list === null) {
			return;
		}

		setIsLoadingMore(true);
		dispatch(
			fetchAiCostCalls(
				new FetchAiCostCalls(accountId, filters.tabQueryParams, list.loadedPage + 1, rows),
			),
		)
			.catch(ignoreHandledError)
			.finally(() => setIsLoadingMore(false));
	}, [accountId, dispatch, filters.tabQueryParams, list, rows]);

	return { list, error, refetch, showMore, isLoadingMore };
};

export default useAiCostCalls;
